import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/Button';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/** The flag the sign-in screen reads once after a deletion. */
export const DELETED_FLAG = 'pa.deleted';

/**
 * You → Privacy (M5.5): export my data as one JSON file, and delete everything after a
 * two-step confirmation (what will be deleted, "I understand", then the button).
 */
export function PrivacyScreen() {
  const { t } = useTranslation();
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [understood, setUnderstood] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportData = async () => {
    setExporting(true);
    setError(null);
    try {
      const data = await api.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'two-export.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError(t('common.error'));
    } finally {
      setExporting(false);
    }
  };

  const deleteEverything = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteMe();
      try {
        sessionStorage.setItem(DELETED_FLAG, '1');
      } catch {
        // the banner is a courtesy
      }
      setMe(null);
      navigate('/signin', { replace: true });
    } catch {
      setError(t('common.error'));
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-6">
      <section aria-label={t('privacy.exportTitle')}>
        <h3 className="text-sm font-semibold text-ink-2">{t('privacy.exportTitle')}</h3>
        <p className="mt-1 text-sm text-ink-2">{t('privacy.exportHint')}</p>
        <div className="mt-3">
          <Button variant="secondary" loading={exporting} onClick={() => void exportData()}>
            {t('privacy.export')}
          </Button>
        </div>
      </section>

      <section aria-label={t('privacy.deleteTitle')} data-delete>
        <h3 className="text-sm font-semibold text-ink-2">{t('privacy.deleteTitle')}</h3>
        {step === 0 ? (
          <>
            <p className="mt-1 text-sm text-ink-2">{t('privacy.deleteHint')}</p>
            <div className="mt-3">
              <Button variant="secondary" onClick={() => setStep(1)}>
                {t('privacy.deleteStart')}
              </Button>
            </div>
          </>
        ) : (
          <div className="mt-2 flex flex-col gap-3 rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated">
            <p className="text-sm text-ink" dir="auto">
              {t('privacy.deleteWhat')}
            </p>
            <ul className="list-disc ps-5 text-sm text-ink-2">
              {(['conversation', 'tasks', 'memory', 'connections', 'sessions'] as const).map(
                (k) => (
                  <li key={k}>{t(`privacy.deleteItems.${k}`)}</li>
                ),
              )}
            </ul>
            <label className="flex min-h-11 items-center gap-3 text-sm text-ink">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="size-5 accent-brand"
              />
              <span>{t('privacy.understand')}</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="critical"
                disabled={!understood || deleting}
                loading={deleting}
                onClick={() => void deleteEverything()}
              >
                {t('privacy.deleteEverything')}
              </Button>
              <Button variant="ghost" disabled={deleting} onClick={() => setStep(0)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </section>
      {error ? (
        <p role="alert" className="text-sm text-critical-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
